<script setup lang="ts">
import type { WikiVehicle } from '~~/types/gamedata';

const props = defineProps<{ raw: WikiVehicle | null }>();

interface Stat {
  label: string;
  value: string | number;
  unit?: string;
}

interface Pill {
  label: string;
  value: string;
}

const DAMAGE_LABELS: Record<string, string> = {
  base: 'Basis',
  physical: 'Physisch',
  energy: 'Energie',
  distortion: 'Verzerrung',
  thermal: 'Thermisch',
  biochemical: 'Biochemisch',
  stun: 'Betäubung',
  cross_section: 'Radarquerschnitt',
  infrared: 'Infrarot',
  electromagnetic: 'Elektromagnetisch',
  fuse: 'Sicherungen',
  components: 'Komponenten',
};

function formatNumber(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits }).format(value);
}

function labelFor(key: string): string {
  return DAMAGE_LABELS[key] ?? key.replaceAll('_', ' ');
}

function rangePercent(range: { minimum?: number | null; maximum?: number | null }): string | null {
  const minimum = range.minimum;
  const maximum = range.maximum;
  if (minimum == null && maximum == null) return null;
  if (minimum != null && maximum != null && minimum !== maximum) {
    return `${formatNumber(minimum * 100)}–${formatNumber(maximum * 100)}%`;
  }
  return `${formatNumber((maximum ?? minimum ?? 0) * 100)}%`;
}

function rangePills(
  values: Record<string, { minimum?: number | null; maximum?: number | null }> | null | undefined,
): Pill[] {
  return Object.entries(values ?? {})
    .map(([key, range]) => ({ label: labelFor(key), value: rangePercent(range) }))
    .filter((pill): pill is Pill => pill.value !== null);
}

function multiplierPills(values: Record<string, number | null> | null | undefined): Pill[] {
  return Object.entries(values ?? {})
    .filter(([key, value]) => !key.endsWith('_change') && value != null)
    .map(([key, value]) => ({ label: labelFor(key), value: `${formatNumber(value!)}×` }));
}

function numberPills(values: Record<string, number | null> | null | undefined): Pill[] {
  return Object.entries(values ?? {})
    .filter(([, value]) => value != null)
    .map(([key, value]) => ({ label: labelFor(key), value: formatNumber(value!) }));
}

function hullLimitPills(
  values: Array<{ HP?: number | null; Name?: string | null; DestructionDamage?: number | null; DetachDamage?: number | null }> | null | undefined,
): Pill[] {
  return (values ?? [])
    .map((item) => {
      const value = item.HP ?? item.DestructionDamage ?? item.DetachDamage;
      if (!item.Name || value == null) return null;
      const label = item.Name
        .replaceAll('_', ' ')
        .replace(/^./, (character) => character.toUpperCase());
      return { label, value: `${formatNumber(value)} HP` };
    })
    .filter((pill): pill is Pill => pill !== null);
}

const primaryStats = computed<Stat[]>(() => [
  props.raw?.health != null
    ? { label: 'Hüllenstruktur', value: formatNumber(props.raw.health), unit: 'HP' }
    : null,
  props.raw?.armor?.health != null
    ? { label: 'Panzerung', value: formatNumber(props.raw.armor.health), unit: 'HP' }
    : null,
  // Schildkapazität/-Regeneration bewusst nicht hier — stehen bereits in der
  // Kurzübersicht oben (ShipexkursShipOverviewPanel compact).
  // Bereits über alle verbauten Power Plants/Kühler aggregiert — dieselben
  // Segment-Kennzahlen wie am einzelnen Bauteil im Komponentenbaum
  // (`getComponentStats()`), hier als Schiffssumme.
  props.raw?.power?.generation_segments != null
    ? { label: 'Energieerzeugung', value: formatNumber(props.raw.power.generation_segments), unit: 'Segmente' }
    : null,
  props.raw?.cooling?.generation_segments != null
    ? { label: 'Kühlungserzeugung', value: formatNumber(props.raw.cooling.generation_segments), unit: 'Segmente' }
    : null,
].filter((stat): stat is Stat => stat !== null));

const shieldDetails = computed<Pill[]>(() => [
  props.raw?.shield?.face_type
    ? { label: 'Schildform', value: props.raw.shield.face_type }
    : null,
  props.raw?.shield?.regeneration_time != null
    ? { label: 'Volle Regeneration', value: `${formatNumber(props.raw.shield.regeneration_time)} s` }
    : null,
  props.raw?.shield?.max_reallocation != null
    ? { label: 'Max. Umleitung', value: `${formatNumber(props.raw.shield.max_reallocation * 100)}%` }
    : null,
  props.raw?.shield?.reconfiguration_cooldown != null
    ? { label: 'Rekonfiguration', value: `${formatNumber(props.raw.shield.reconfiguration_cooldown)} s` }
    : null,
  props.raw?.shield?.max_electrical_charge_damage_rate != null
    ? { label: 'Elektrische Schadensrate', value: formatNumber(props.raw.shield.max_electrical_charge_damage_rate) }
    : null,
].filter((pill): pill is Pill => pill !== null));

const shieldResistancePills = computed(() => rangePills(props.raw?.shield?.resistance));
const shieldAbsorptionPills = computed(() => rangePills(props.raw?.shield?.absorption));
const armorDamagePills = computed(() => multiplierPills(props.raw?.armor?.damage_multiplier));
const armorResistancePills = computed(() => multiplierPills(props.raw?.armor?.resistance_multiplier));
const armorDeflectionPills = computed(() => numberPills(props.raw?.armor?.deflection));
const armorPenetrationPills = computed(() => numberPills(props.raw?.armor?.penetration_resistance));
const armorSignalPills = computed(() => multiplierPills(props.raw?.armor?.signal_multiplier));
const hullDestructionPills = computed(() => hullLimitPills(props.raw?.damage_limits?.before_destruction));

// Radar-Querschnitt front/side/top = length/width/height der Bounding Box —
// als kompakte Zeile statt Einzel-Pills (siehe FleetYards/erkul-Referenzstil).
const crossSectionSummary = computed(() => {
  const cs = props.raw?.cross_section;
  if (cs?.length == null || cs.width == null || cs.height == null) return null;
  return { front: formatNumber(cs.length), side: formatNumber(cs.width), top: formatNumber(cs.height) };
});

// Nur die "Schilde aktiv"-Basiswerte (Normalzustand) statt aller vier
// Zustandskombinationen (siehe `WikiVehicle.signature`) — deckungsgleich mit
// `emission.ir`/`emission.em_idle`, die die API selbst als "IR/EM mit
// aktiven Schilden" dokumentiert.
const signatureSummary = computed(() => {
  const signature = props.raw?.signature;
  if (signature?.ir_shields == null && signature?.em_shields == null) return null;
  return {
    ir: signature?.ir_shields != null ? formatNumber(signature.ir_shields) : null,
    em: signature?.em_shields != null ? formatNumber(signature.em_shields) : null,
  };
});

const penetrationPills = computed(() => multiplierPills(props.raw?.penetration_multiplier));

const distribution = computed(() => getShipDistribution(props.raw));

const hasData = computed(() => [
  primaryStats.value,
  shieldDetails.value,
  shieldResistancePills.value,
  shieldAbsorptionPills.value,
  armorDamagePills.value,
  armorResistancePills.value,
  armorDeflectionPills.value,
  armorPenetrationPills.value,
  armorSignalPills.value,
  hullDestructionPills.value,
  penetrationPills.value,
  distribution.value,
].some((values) => values.length) || !!crossSectionSummary.value || !!signatureSummary.value);
</script>

<template>
  <ShipexkursStatCard title="Gefechtswerte" icon="shieldgenerator">
    <div v-if="hasData" class="space-y-6">
      <!-- Kurzübersicht: Allgemeine Performance + Signatur nebeneinander, ganz
      oben über Hülle/Schilden — verdichtete Fassung der "Allgemein"-Performance-Tab
      (ShipexkursShipOverviewPanel `compact`) plus IR/EM/Querschnitt. -->
      <div v-if="raw" class="@container">
        <div class="flex flex-wrap gap-x-10 gap-y-5">
          <div class="w-full @lg:w-[calc(50%-1.25rem)]">
            <ShipexkursShipOverviewPanel :raw="raw" compact />
          </div>
          <div v-if="crossSectionSummary || signatureSummary" class="w-full @lg:w-[calc(50%-1.25rem)] space-y-3">
            <div v-if="signatureSummary" class="flex flex-wrap items-center gap-6">
              <ShipexkursStatRow v-if="signatureSummary.ir" icon="infrared" :value="signatureSummary.ir" unit="IR" icon-class="text-error-400" />
              <ShipexkursStatRow v-if="signatureSummary.em" ui-icon="i-lucide-zap" :value="signatureSummary.em" unit="EM" icon-class="text-warning-400" />
            </div>
            <div v-if="crossSectionSummary" class="flex flex-wrap items-center gap-2">
              <ShipexkursSystemIcon name="crosssection" class="size-6 shrink-0 text-aris-400" />
              <span class="flex items-center gap-1.5 rounded bg-white/5 px-2 py-1 text-sm">
                <span class="text-neutral-500">Front</span>
                <span class="font-medium text-white">{{ crossSectionSummary.front }}</span>
              </span>
              <span class="flex items-center gap-1.5 rounded bg-white/5 px-2 py-1 text-sm">
                <span class="text-neutral-500">Seite</span>
                <span class="font-medium text-white">{{ crossSectionSummary.side }}</span>
              </span>
              <span class="flex items-center gap-1.5 rounded bg-white/5 px-2 py-1 text-sm">
                <span class="text-neutral-500">Oben</span>
                <span class="font-medium text-white">{{ crossSectionSummary.top }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="primaryStats.length" class="grid gap-5 border-t border-white/10 pt-5 sm:grid-cols-2 xl:grid-cols-4">
         <ShipexkursStatHero
          v-for="stat in primaryStats"
          :key="stat.label"
          :label="stat.label"
          :value="stat.value"
          :unit="stat.unit"
        />
      </div>

      <!-- DISTRIBUTION DISABLED - PROPULSION VALUES NOT WORKING -->
      <!-- <div v-if="distribution.length" class="grid gap-5 border-t border-white/10 pt-5 sm:grid-cols-3">
        <ShipexkursDistributionBar v-for="bar in distribution" :key="bar.key" :bar="bar" />
      </div>
      <div v-if="distribution.length" class="-mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-400">
        <span v-for="bucket in (['weapons', 'systems', 'propulsion'] as const)" :key="bucket" class="flex items-center gap-1.5">
          <span class="size-2 shrink-0 rounded-full" :style="{ backgroundColor: DISTRIBUTION_BUCKET_COLORS[bucket] }" />
          {{ DISTRIBUTION_BUCKET_LABELS[bucket] }}
        </span>
      </div> -->

      <div
        v-if="shieldDetails.length || shieldResistancePills.length || shieldAbsorptionPills.length || armorDamagePills.length || armorResistancePills.length || armorDeflectionPills.length || armorPenetrationPills.length || hullDestructionPills.length || armorSignalPills.length || penetrationPills.length"
        class="grid gap-4 border-t border-white/10 pt-5 xl:grid-cols-2"
      >
        <section v-if="armorDamagePills.length || armorResistancePills.length || armorDeflectionPills.length || armorPenetrationPills.length || hullDestructionPills.length || armorSignalPills.length || penetrationPills.length" class="rounded-lg bg-white/[0.03] p-4">
          <h4 class="!my-0 mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-aris-400">
            <ShipexkursSystemIcon name="shieldgenerator" class="size-3.5" />
            Hülle & Panzerung
          </h4>
          <ShipexkursDefensePillGroup label="Zerstörungsschwellen" :items="hullDestructionPills" />
          <ShipexkursDefensePillGroup label="Schadensmultiplikator" :items="armorDamagePills" class="mt-3" />
          <ShipexkursDefensePillGroup label="Widerstandsmultiplikator" :items="armorResistancePills" class="mt-3" />
          <ShipexkursDefensePillGroup label="Ablenkung" :items="armorDeflectionPills" class="mt-3" />
          <ShipexkursDefensePillGroup label="Penetrationswiderstand" :items="armorPenetrationPills" class="mt-3" />
          <ShipexkursDefensePillGroup label="Signaturmultiplikator" :items="armorSignalPills" class="mt-3" />
          <ShipexkursDefensePillGroup label="Penetrationsmultiplikator" :items="penetrationPills" class="mt-3" />
        </section>

        <section v-if="shieldDetails.length || shieldResistancePills.length || shieldAbsorptionPills.length" class="rounded-lg bg-white/[0.03] p-4">
          <h4 class="!my-0 mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-aris-400">
            <ShipexkursSystemIcon name="shieldgenerator" class="size-3.5" />
            Schilde
          </h4>
          <ShipexkursDefensePillGroup label="System" :items="shieldDetails" />
          <ShipexkursDefensePillGroup label="Resistenz" :items="shieldResistancePills" class="mt-3" />
          <ShipexkursDefensePillGroup label="Absorption" :items="shieldAbsorptionPills" class="mt-3" />
        </section>
      </div>
    </div>

    <p v-else class="!my-0 text-sm text-neutral-500">Keine Gefechtswerte verfügbar.</p>
  </ShipexkursStatCard>
</template>
